import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';

const API_BASE = `${config.api.baseUrl}/billing`;

function headers(): HeadersInit {
  return {
    Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}`,
    'Content-Type': 'application/json',
  };
}

async function unwrap(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.message || `Request failed: ${response.statusText}`);
  return body.data;
}

export type SubscriptionTier = 'premium' | 'premium_plus';

export interface SubscriptionState {
  hasSubscription: boolean;
  status: string | null;
  tier: SubscriptionTier | null;
  /** True when cancelled but still inside the paid period. */
  cancelAtPeriodEnd: boolean;
  /** ISO date the current paid period ends. */
  currentPeriodEnd: string | null;
  isPremium: boolean;
  /** Live and not already winding down. */
  canCancel: boolean;
  /** Winding down but not yet ended — a pending cancellation can be undone. */
  canResume: boolean;
  /** Ended for good; needs a fresh checkout rather than a resume. */
  canResubscribe: boolean;
  /**
   * A plan change already booked for the renewal date — a downgrade waiting
   * out the period the user has paid for. Null when nothing is scheduled.
   */
  pendingTier: SubscriptionTier | null;
  /** ISO date the pending change takes effect. */
  pendingTierEffectiveAt: string | null;
}

export interface CheckoutResult {
  /**
   * Hosted Stripe Checkout URL to redirect to — or null when no purchase was
   * needed because the account already had a subscription and the server
   * changed the plan in place instead. See planChanged.
   */
  url: string | null;
  introApplied?: boolean;
  /** True when an existing subscription was moved rather than a new one sold. */
  planChanged?: boolean;
  tier?: SubscriptionTier;
  effective?: 'now' | 'period_end';
  effectiveAt?: string | null;
}

export interface PlanChangeResult {
  changed: boolean;
  tier: SubscriptionTier;
  /** 'now' for an upgrade, 'period_end' for a scheduled downgrade. */
  effective: 'now' | 'period_end';
  effectiveAt: string | null;
}

const NO_SUBSCRIPTION: SubscriptionState = {
  hasSubscription: false,
  status: null,
  tier: null,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  isPremium: false,
  canCancel: false,
  canResume: false,
  canResubscribe: true,
  pendingTier: null,
  pendingTierEffectiveAt: null,
};

class BillingService {
  /**
   * Starts a real Stripe Checkout session for the given tier and returns the
   * hosted checkout URL to redirect the browser to.
   */
  async createCheckoutSession(tier: SubscriptionTier): Promise<CheckoutResult> {
    const response = await fetch(`${API_BASE}/create-checkout-session`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ tier }),
    });
    // The whole payload, not just the URL: the server answers a checkout
    // request from an existing subscriber by changing their plan instead, and
    // that reply has no URL to redirect to.
    return await unwrap(response);
  }


  /**
   * Current subscription state, used to decide which of Cancel / Reactivate /
   * Subscribe the profile menu offers. Read live from Stripe server-side, so
   * a pending cancellation is visible — our own columns cannot distinguish it
   * from a healthy subscription.
   */
  async getSubscription(): Promise<SubscriptionState> {
    try {
      const response = await fetch(`${API_BASE}/subscription`, { headers: headers() });
      const data = await unwrap(response);
      return { ...NO_SUBSCRIPTION, ...data };
    } catch {
      // The menu must still render if billing is unreachable.
      return NO_SUBSCRIPTION;
    }
  }

  /**
   * Cancels at period end; the user keeps access until the date they paid
   * through. The retention survey's answers ride along and are stored against
   * the cancellation — they used to go to console.log and nowhere else.
   */
  async cancelSubscription(
    reason?: string,
    feedback?: string,
  ): Promise<{ cancelled: boolean; accessUntil: string | null }> {
    const response = await fetch(`${API_BASE}/cancel`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ reason, feedback }),
    });
    return await unwrap(response);
  }

  /** Undoes a pending cancellation while the period is still running. */
  async resumeSubscription(): Promise<{ resumed: boolean; renewsOn: string | null }> {
    const response = await fetch(`${API_BASE}/resume`, { method: 'POST', headers: headers() });
    return await unwrap(response);
  }

  /**
   * Move to another plan without leaving the app.
   *
   * Upgrades take effect immediately; downgrades are scheduled for the
   * renewal date, so the user keeps the plan they have paid for until it runs
   * out. The response says which happened, and when.
   */
  async changePlan(tier: SubscriptionTier): Promise<PlanChangeResult> {
    const response = await fetch(`${API_BASE}/change-plan`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ tier }),
    });
    return await unwrap(response);
  }

  /** Calls off a scheduled downgrade, keeping the current plan. */
  async cancelScheduledPlanChange(): Promise<{ cancelled: boolean }> {
    const response = await fetch(`${API_BASE}/change-plan`, {
      method: 'DELETE',
      headers: headers(),
    });
    return await unwrap(response);
  }

  /**
   * Opens the Stripe Billing Portal. Kept for payment-method and invoice
   * management, which still live on Stripe's side; plan changes no longer use
   * it — see changePlan.
   */
  async createPortalSession(): Promise<string> {
    const response = await fetch(`${API_BASE}/create-portal-session`, {
      method: 'POST',
      headers: headers(),
    });
    const data = await unwrap(response);
    return data.url;
  }
}

export const billingService = new BillingService();
